import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Checkbox, Badge, Select } from '@mantine/core';
import { IconSend, IconMail } from '@tabler/icons-react';
import { Business } from '@turizi/shared';
import { useSendAssignmentsEmail } from '@/hooks/api-hooks';
import { localTenant } from '@/services/session-service';

interface Props {
    opened: boolean;
    onClose: () => void;
    business: Business | null;
    date: string;
    reservationsCount: number;
}

export default function SendAssignmentMailModal({
    opened,
    onClose,
    business,
    date,
    reservationsCount,
}: Props): JSX.Element {
    const { t } = useTranslation();

    const tenant = localTenant();
    const senderEmails = Array.from(
        new Set([
            tenant?.settings?.email,
            ...(tenant?.settings?.sender_emails || [])
        ].filter(Boolean))
    );
    const [selectedFromEmail, setSelectedFromEmail] = useState<string>(senderEmails[0] || '');

    useEffect(() => {
        if (senderEmails.length > 0 && !selectedFromEmail) {
            setSelectedFromEmail(senderEmails[0]);
        }
    }, [senderEmails]);

    const [selectedRecipientEmails, setSelectedRecipientEmails] = useState<string[]>([]);
    const { mutate: sendAssignmentsEmail, isPending: isSendingEmail } = useSendAssignmentsEmail();

    const businessEmails = useMemo(() => {
        if (!business?.emails) return [];
        return Object.values(business.emails).filter((e) => typeof e === 'string' && e.trim() !== '') as string[];
    }, [business]);

    const toggleEmailSelection = (email: string) => {
        if (selectedRecipientEmails.includes(email)) {
            setSelectedRecipientEmails(selectedRecipientEmails.filter((e) => e !== email));
        } else {
            setSelectedRecipientEmails([...selectedRecipientEmails, email]);
        }
    };

    const handleSendEmail = () => {
        if (!business || selectedRecipientEmails.length === 0) return;

        sendAssignmentsEmail(
            {
                businessId: business.id,
                date: date,
                manualEmails: selectedRecipientEmails,
                reservationsCount: reservationsCount,
                fromEmail: selectedFromEmail
            },
            {
                onSuccess: () => {
                    onClose();
                },
            }
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={t('send_email_to_business')}
            centered
            size="md"
        >
            <div className="space-y-4">
                {senderEmails.length > 0 && (
                    <Select
                        label={t('select_sender_email')}
                        data={senderEmails}
                        value={selectedFromEmail}
                        onChange={(val) => setSelectedFromEmail(val || '')}
                        allowDeselect={false}
                        size="xs"
                    />
                )}

                {/* Business & Summary Card */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                    <div className="font-bold text-sm text-slate-900 flex items-center justify-between">
                        <span>{business?.name}</span>
                    </div>
                    <div className="text-xs text-slate-600 font-medium">
                        {t('reservations')}: {reservationsCount}
                    </div>
                </div>

                {/* Recipient Emails Selection */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        {/* {businessEmails.length > 0 && (
                            <Button
                                variant="subtle"
                                color="gray"
                                size="compact-xs"
                                onClick={() => {
                                    if (selectedRecipientEmails.length === businessEmails.length) {
                                        setSelectedRecipientEmails([]);
                                    } else {
                                        setSelectedRecipientEmails([...businessEmails]);
                                    }
                                }}
                            >
                                {selectedRecipientEmails.length === businessEmails.length ? t('deselect_all') : t('select_all')}
                            </Button>
                        )} */}
                    </div>

                    {businessEmails.length === 0 ? (
                        <div className="text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
                            {t('no_emails_configured')}
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2 bg-white border border-gray-100 rounded-lg p-2.5">
                            {businessEmails.map((email) => (
                                <div
                                    key={email}
                                    className="flex items-center justify-between py-1 px-2 rounded-md hover:bg-slate-50 cursor-pointer"
                                    onClick={() => toggleEmailSelection(email)}
                                >
                                    <div className="flex items-center gap-2.5">
                                        <IconMail size={16} className="text-gray-400" />
                                        <span className="text-xs font-medium text-gray-800">{email}</span>
                                    </div>
                                    <Checkbox
                                        checked={selectedRecipientEmails.includes(email)}
                                        onChange={() => { }}
                                        size="xs"
                                        color='black'
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex justify-between gap-2 pt-2 border-t border-gray-100">
                    <Button
                        variant="subtle"
                        color="black"
                        size="xs"
                        onClick={onClose}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        size="xs"
                        color="black"
                        leftSection={<IconSend size={14} />}
                        loading={isSendingEmail}
                        disabled={selectedRecipientEmails.length === 0}
                        onClick={handleSendEmail}
                    >
                        {t('send_email')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
